import { withEntityTitle } from '@/components/workflows/docProvenance';
import { sealBlockHtml, SEAL_STYLE_CSS, type SealContext } from '@/components/workflows/sealModel';
import type { AppointWorkflowData } from './appointContent';
import { BANK_INK_REASON, type SigningMode } from '../signingModel';

/**
 * DRAFT document builders for the "Appoint an Officer" workflow.
 *
 * Every paper here is produced as a clearly marked DRAFT and waits on approval
 * before this workflow goes live. Plain spoken register, no dashes, no internal
 * codes, and true dates only.
 */

export const OFFICER_FORMATS_APPROVED = true;

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string): string {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  ol li, ul li { margin-bottom: 8px; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ${SEAL_STYLE_CSS}
`;

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  <div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">The date on this page is the true date it is signed.</p>
</body>
</html>`;
}


/** Renders the seal only when it is actually applied. Otherwise nothing at all. */
function sealSpot(ctx: SealContext): string {
  return sealBlockHtml(ctx);
}

export interface AppointBuildContext {
  data: AppointWorkflowData;
  associationName: string;
  associationOfficerRole: string;
  date?: string;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

export interface AppointOutput {
  key: string;
  title: string;
  html: string;
  requiresSignature: boolean;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
}

function personName(ctx: AppointBuildContext): string {
  return esc(ctx.data.person_name || "[Person's name]");
}

function officeName(ctx: AppointBuildContext): string {
  return esc(ctx.data.office || '[Office]');
}

function sigBlock(label: string): string {
  return `<div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(label)}</div>
    <div class="sig-line" style="margin-top:34px; width:200px"></div>
    <div class="sig-label">Date signed</div>
  </div>`;
}

export function buildAppointmentResolutionHtml(ctx: AppointBuildContext): string {
  const effective = longDate(ctx.data.effective_date);
  const inner = `
  <p>${esc(ctx.associationName)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The action</h2>
  <p>${esc(ctx.associationName)} appoints ${personName(ctx)} to the office of ${officeName(ctx)}, effective ${esc(effective)}, with the duties the Constitution and the governing documents give that office.</p>
  <h2>What this does not do</h2>
  <p>This appointment grants the office and nothing else. Banking authority is a separate grant, made only by its own resolution.</p>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Appointment Resolution', 'The Association appoints a person to an office', inner);
}

export function buildOfficerAcceptanceHtml(ctx: AppointBuildContext): string {
  const effective = longDate(ctx.data.effective_date);
  const inner = `
  <p>I, ${personName(ctx)}, accept the office of ${officeName(ctx)}. I have read what this office carries in the Constitution and the governing documents, and I take up its duties as of ${esc(effective)}.</p>
  ${sigBlock('Person accepting the office')}`;
  return page('Officer Acceptance', 'One page, signed by the person taking the office', inner);
}

export function buildBankingAuthorityResolutionHtml(ctx: AppointBuildContext): string {
  const effective = longDate(ctx.data.effective_date);
  const accounts = ctx.data.banking_accounts || '[The accounts named]';
  const inner = `
  <p>${esc(ctx.associationName)} takes this action on ${esc(longDate(ctx.date))}.</p>
  <h2>The action</h2>
  <p>Banking authority is granted to ${personName(ctx)} on the following accounts, effective ${esc(effective)}:</p>
  <p>${esc(accounts)}</p>
  <h2>The terms of the grant</h2>
  <ul>
    <li>This grant is separate from the office and stands on its own.</li>
    <li>It reaches only the accounts named above.</li>
    <li>It is revocable at any time by resolution of the Association.</li>
    <li>The bank is asked to update its signature records to match this grant.</li>
  </ul>
  ${sigBlock(`${ctx.associationOfficerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Banking Authority Resolution', 'A separate grant of authority on named accounts', inner);
}

function buildAppointOutputsInternal(ctx: AppointBuildContext): AppointOutput[] {
  const { data } = ctx;
  if (data.is_member !== 'yes') return [];
  const outputs: AppointOutput[] = [
    {
      key: 'officer_appointment_resolution',
      title: 'Appointment Resolution',
      html: buildAppointmentResolutionHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: false,
    },
    {
      key: 'officer_acceptance',
      title: 'Officer Acceptance',
      html: buildOfficerAcceptanceHtml(ctx),
      requiresSignature: true,
      signing: 'stewards_choice',
      defaultInk: false,
    },
  ];
  if (data.grant_banking === 'yes') {
    outputs.push({
      key: 'officer_banking_authority_resolution',
      title: 'Banking Authority Resolution',
      html: buildBankingAuthorityResolutionHtml(ctx),
      requiresSignature: true,
      signing: 'wet_required',
      signingReason: BANK_INK_REASON,
    });
  }
  return outputs;
}

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildAppointOutputs(ctx: AppointBuildContext): AppointOutput[] {
  const entityName = ctx.associationName;
  return buildAppointOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
